// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiMealCategorizer.js

class AiMealCategorizer {
  constructor(config = {}) {
    // ---- Injected dependencies (no hard-coding) ----
    this.serviceName   = config.serviceName || process.env.AI_MEAL_CATEGORIZER_SERVICE_NAME || 'AiMealCategorizer';
    this.maxHopCount   = Number(config.maxHopCount ?? (process.env.AI_MEAL_CATEGORIZER_MAX_HOPS || 8));
    this.allowedRoutes = config.allowedRoutes
      || safeJson(process.env.AI_MEAL_CATEGORIZER_ALLOWED_ROUTES)
      || {}; // empty means "allow nothing" unless passed per call

    this.taxonomy      = config.taxonomy
      || safeJson(process.env.AI_MEAL_CATEGORIZER_TAXONOMY_JSON)
      || AiMealCategorizer.getDefaultTaxonomy(); // you can replace this at runtime via setTaxonomy()

    this.timeProvider  = typeof config.timeProvider === 'function' ? config.timeProvider : () => new Date();
    this.idProvider    = typeof config.idProvider === 'function' ? config.idProvider : defaultUlid;
    this.idempotency   = config.idempotency || { tryStart: () => true, markComplete: () => {} };

    // Lexicon cache holder on taxonomy to avoid rebuilds across instances
    this.taxonomy._drinkLexiconRegex = null;
  }

  setTaxonomy(next) {
    if (!next || typeof next !== 'object') throw new Error('taxonomy must be an object');
    this.taxonomy = next;
    this.taxonomy._drinkLexiconRegex = null; // reset cache
  }

  /**
   * @param {string} text
   * @param {{
   *   timestampStrategy?: 'utc_midnight'|'now',
   *   ingress?: string,
   *   egress?: string,
   *   eventContext?: {
   *     requestId?: string,
   *     traceId?: string,
   *     visited?: string[],
   *     hopCount?: number,
   *     idempotencyKey?: string,
   *     changeHash?: string,
   *     entityId?: string
   *   }
   * }} options
   */
  categorize(text, options = {}) {
    // self-heal if HMR detached methods
    this._ensurePrototypeMethods();

    const timestampStrategy = options.timestampStrategy || 'utc_midnight';
    const ingress = options.ingress || process.env.AI_MEAL_CATEGORIZER_DEFAULT_INGRESS || 'categorizer_ingress';
    const egress  = options.egress  || process.env.AI_MEAL_CATEGORIZER_DEFAULT_EGRESS  || 'categorizer_egress';

    const ec = options.eventContext || {};
    const event = this._newEvent({
      ingress,
      egress,
      payload: { text: String(text || '') },
      requestId: ec.requestId,
      traceId: ec.traceId,
      visited: ec.visited,
      hopCount: ec.hopCount,
      idempotencyKey: ec.idempotencyKey,
      changeHash: ec.changeHash,
      entityId: ec.entityId
    });

    // ---------------- Event guardrails ----------------
    // Drop self/seen
    if (event.source === this.serviceName || (event.visited || []).includes(this.serviceName)) {
      return this._resultSkeleton({ reason: 'echo_or_seen' }, event);
    }

    // Enforce ingress→egress allow-list
    const list = (this.allowedRoutes && this.allowedRoutes[event.ingress]) || [];
    if (!list.includes(event.egress)) {
      return this._resultSkeleton({ reason: 'egress_not_allowed' }, event);
    }

    // Echo/route safety
    if (event.ingress === event.egress) {
      return this._resultSkeleton({ reason: 'same_ingress_blocked' }, event);
    }

    // Idempotency gate (if provided)
    if (event.idempotencyKey && this.idempotency && typeof this.idempotency.tryStart === 'function') {
      const ok = !!this.idempotency.tryStart(event.idempotencyKey);
      if (!ok) return this._resultSkeleton({ reason: 'duplicate' }, event);
    }

    // Hop safety
    const hopCount = Number(event.hopCount || 0) + 1;
    if (hopCount > this.maxHopCount) {
      return this._resultSkeleton({ reason: 'hop_count_exceeded' }, event);
    }

    // "Publish": append self and increment hopCount in returned meta (never actually emits)
    const guardedEvent = { ...event, visited: [...(event.visited || []), this.serviceName], hopCount };

    // ------------- Categorization pipeline -------------
    const norm = this._normalize(event.payload.text);
    if (!norm) return this._resultSkeleton({ reason: 'empty_text' }, guardedEvent);

    const sentences = this._splitSentences(norm);
    const buckets = this._segmentByCues(sentences);

    const mainsR  = this._parseDishBucket(buckets.mains, { allowAppendToNameIfMadeWith: true });
    const sidesR  = this._parseDishBucket(buckets.sides, { allowAppendToNameIfMadeWith: false });
    const drinksR = this._parseDrinkBucket(buckets.drinks);

    const reb = this._rebalanceMainsSides(mainsR.items, sidesR.items);

    const now = this.timeProvider();
    const ts = timestampStrategy === 'now'
      ? now.toISOString()
      : this._utcMidnightIso(now);

    const result = {
      accepted: true,
      reason: null,
      timestampIso: ts,
      categories: {
        mains: reb.mains,
        sides: reb.sides,
        drinks: drinksR.items,
        unknown: [...mainsR.unknown, ...sidesR.unknown, ...drinksR.unknown].map(this._toCanonical.bind(this)),
      },
      meta: {
        event: sanitizeEventForReturn(guardedEvent)
      }
    };

    // Mark idempotent success if applicable
    if (event.idempotencyKey && this.idempotency && typeof this.idempotency.markComplete === 'function') {
      try { this.idempotency.markComplete(event.idempotencyKey); } catch {}
    }

    return result;
  }

  // ---------- Parsing ----------

  _parseDishBucket(sentences, { allowAppendToNameIfMadeWith }) {
    const out = [];
    const unknown = [];
    let lastItem = null;

    for (const s of sentences) {
      const phrases = this._phraseSplit(s);
      for (const rawPhrase of phrases) {
        const phrase = rawPhrase.trim();
        if (!phrase) continue;

        const madeWithFlag = /made with/i.test(phrase);
        const parsed = this._splitBaseAndWith(phrase);

        const item = this._toDishItem(parsed.base);

        // Attachments → ingredients/seasonings/modifiers
        const explodedAll = [];
        for (const attRaw of parsed.attachments) {
          const exploded = this._explodeAttachment(attRaw);
          explodedAll.push(...exploded);
          for (const pRaw of exploded) {
            const p = this._stripLeadingArticles(pRaw);
            if (!p) continue;

            if (this._isSeasoning(p)) {
              item.seasonings.push(this._toCanonical(p));
            } else if (this._isQuantityPhrase(p)) {
              item.modifiers.push(this._toCanonical(p));
            } else {
              item.ingredients.push(this._toCanonical(p));
            }
          }
        }

        // Promote egg quantity if base is an egg dish and quantity lives in attachments
        if (!item.quantity && /\beggs?\b/i.test(item.name)) {
          const eggQty = explodedAll.find(x => /\beggs?\b/i.test(x) && this._isQuantityPhrase(x));
          if (eggQty) {
            const can = this._toCanonical(eggQty);
            item.quantity = can;
            item.modifiers = item.modifiers.filter(m => m.toLowerCase() !== can.toLowerCase());
          }
        }

        // If phrase looks like a pure tail and we have a last dish, attach to it
        if (!this._looksLikeDish(item) && lastItem) {
          const tails = this._explodeAttachment(phrase);
          let attachedSomething = false;
          for (const tRaw of tails) {
            const t = this._stripLeadingArticles(tRaw);
            if (!t) continue;
            if (this._isSeasoning(t)) { lastItem.seasonings.push(this._toCanonical(t)); attachedSomething = true; }
            else if (this._isQuantityPhrase(t)) { lastItem.modifiers.push(this._toCanonical(t)); attachedSomething = true; }
            else { lastItem.ingredients.push(this._toCanonical(t)); attachedSomething = true; }
          }
          if (!attachedSomething) unknown.push(this._toCanonical(phrase));
          continue;
        }

        if (!this._looksLikeDish(item) && !lastItem) {
          unknown.push(this._toCanonical(phrase));
          continue;
        }

        // Optionally append ingredients to the name (only for "made with ..." mains like eggs)
        if (allowAppendToNameIfMadeWith && (madeWithFlag || /\beggs?\b/i.test(item.name))) {
          const ingTail = item.ingredients.join(' ').trim();
          if (ingTail) item.name = `${item.name} ${ingTail}`.trim();
          item.name = this._collapseSpaces(item.name);
        }

        out.push(this._dedupeArrays(item));
        lastItem = out[out.length - 1];
      }
    }
    return { items: out, unknown };
  }

  _parseDrinkBucket(sentences) {
    const out = [];
    const unknown = [];
    let current = null;

    for (const s of sentences) {
      const phrases = this._phraseSplit(s);
      for (const raw of phrases) {
        const phrase = raw.trim();
        if (!phrase) continue;

        const parsed = this._splitBaseAndWith(phrase);
        const base = this._guessBaseDrink(parsed.base);

        if (base) {
          const drink = {
            name: base.name,
            quantity: this._normalizeQuantityForDrinks(base.quantity, base.name),
            modifiers: [],
            toppings: [],
            type: 'drink',
          };
          for (const att of parsed.attachments) {
            const parts = this._explodeAttachment(att);
            for (const pRaw of parts) {
              const p = this._stripLeadingArticles(pRaw);
              if (!p) continue;
              if (this._isQuantityPhrase(p)) {
                drink.quantity = drink.quantity ? `${drink.quantity}; ${this._toCanonical(p)}` : this._toCanonical(p);
              } else if (this._isDrinkModifier(p)) {
                drink.modifiers.push(this._toCanonical(p));
              } else if (this._inTaxFuzzy('drinks', p)) {
                out.push(this._dedupeArrays(drink));
                current = {
                  name: this._stemDrinkName(p),
                  quantity: null,
                  modifiers: [],
                  toppings: [],
                  type: 'drink',
                };
              } else {
                unknown.push(this._toCanonical(p));
              }
            }
          }
          out.push(this._dedupeArrays(drink));
          current = drink;
          continue;
        }

        if (current) {
          const parts = this._explodeAttachment(phrase);
          let attached = false;
          for (const pRaw of parts) {
            const p = this._stripLeadingArticles(pRaw);
            if (!p) continue;
            if (this._isQuantityPhrase(p)) {
              current.quantity = current.quantity ? `${current.quantity}; ${this._toCanonical(p)}` : this._toCanonical(p);
              attached = true;
            } else if (this._isDrinkModifier(p)) {
              current.modifiers.push(this._toCanonical(p));
              attached = true;
            } else {
              unknown.push(this._toCanonical(p));
            }
          }
          if (!attached && parts.length === 0) unknown.push(this._toCanonical(phrase));
        } else {
          const maybe = this._guessBaseDrink(phrase);
          if (maybe) {
            out.push(this._dedupeArrays({
              name: maybe.name,
              quantity: this._normalizeQuantityForDrinks(maybe.quantity, maybe.name),
              modifiers: [],
              toppings: [],
              type: 'drink',
            }));
            current = out[out.length - 1];
          } else {
            unknown.push(this._toCanonical(phrase));
          }
        }
      }
    }
    // Post-pass
    for (const d of out) {
      if (/^water$/i.test(d.name)) d.quantity = null;
      d.modifiers = (d.modifiers || []).map(m => m.replace(/^a\s+/i, '').trim());
    }
    return { items: out, unknown };
  }

  // ---------- Helpers ----------

  _ensurePrototypeMethods() {
    const needs = ['_parseDishBucket', '_parseDrinkBucket', '_segmentByCues', '_guessBaseDrink', '_isLikelyDrinkSentence'];
    for (const k of needs) {
      if (typeof this[k] !== 'function' && typeof AiMealCategorizer.prototype[k] === 'function') {
        this[k] = AiMealCategorizer.prototype[k].bind(this);
      }
    }
  }

  _dedupeArrays(item) {
    const uniq = (arr) => {
      const seen = new Set();
      const out = [];
      for (const value of arr || []) {
        const key = this._toCanonical(String(value)).toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(this._toCanonical(String(value)));
      }
      return out;
    };
    return {
      ...item,
      ingredients: uniq(item.ingredients),
      seasonings: uniq(item.seasonings),
      modifiers: uniq(item.modifiers),
    };
  }

  _normalize(s) {
    return String(s || '')
      .replace(/\s+/g, ' ')
      .replace(/\s([,.!?:;])/g, '$1')
      .trim();
  }

  _splitSentences(s) {
    return s
      .split(/(?<=[.!?])\s+(?=[A-Z])|(?<=,)\s+(?=To drink|to drink|Alongside|alongside|Served with|served with)/)
      .map(x => x.trim())
      .filter(Boolean);
  }

  _segmentByCues(sentences) {
    const mains = [];
    const sides = [];
    const drinks = [];
    for (const sent of sentences) {
      const l = sent.toLowerCase();

      // explicit cues
      if (/(^|\b)(to drink|drink|beverage|sip|sipping|drank|drunk)\b/.test(l)) { drinks.push(sent); continue; }
      if (/\b(alongside|on the side|side dish|side)\b/.test(l)) { sides.push(sent); continue; }
      if (/\bserved with\b/.test(l) && /\byogurt|yoghurt\b/.test(l)) { sides.push(sent); continue; }

      // tolerant drink detection
      if (this._isLikelyDrinkSentence(l)) { drinks.push(sent); continue; }

      mains.push(sent);
    }
    return { mains, sides, drinks };
  }

  _isLikelyDrinkSentence(l) {
    const hasContainer = /\b(cup|mug|glass|bottle|can|shot|pint|pitcher|thermos|flask|carton|tumbler)\b/.test(l);
    const drinkTokens = this.taxonomy._drinkLexiconRegex || this._buildDrinkLexiconRegex();
    const hasDrinkToken = drinkTokens.test(l);
    const drinkVerbs = /\b(sip|sipping|drank|drink|drinking|hydrate|hydrating|gulp|chug|poured)\b/.test(l);
    return hasDrinkToken || (hasContainer && (drinkVerbs || hasDrinkToken));
  }

  _buildDrinkLexiconRegex() {
    const tokens = (this.taxonomy.drinks || []).slice().sort((a,b) => b.length - a.length);
    const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = tokens.map(esc).join('|');
    const re = new RegExp(`\\b(${pattern})\\b`, 'i');
    this.taxonomy._drinkLexiconRegex = re;
    return re;
  }

  _phraseSplit(s) {
    return s
      .split(/[,;](?![^()]*\))/)
      .flatMap(x => x.split(/\band\b/i))
      .map(x => x.trim())
      .filter(Boolean);
  }

  _splitBaseAndWith(phrase) {
    const withCues = /(made with|served with|topped with|seasoned with|with)\b/i;
    const idx = phrase.search(withCues);
    if (idx === -1) return { base: phrase, attachments: [] };

    const base = phrase.slice(0, idx).trim().replace(/[.,]$/, '');
    const tail = phrase.slice(idx).replace(withCues, '').trim();
    const attachments = this._explodeAttachment(tail);
    return { base: base || phrase, attachments };
  }

  _explodeAttachment(s) {
    const normalized = String(s || '').replace(/\bseasoned with\b/gi, ',');
    return normalized
      .split(/[,;](?![^()]*\))|\band\b/i)
      .map(x => x.trim())
      .filter(Boolean);
  }

  _toDishItem(basePhrase) {
    const name = this._stemDishName(basePhrase);
    const quantity = this._extractQuantity(basePhrase);
    return {
      name,
      quantity,
      ingredients: [],
      seasonings: [],
      modifiers: [],
      type: 'dish',
    };
  }

  _looksLikeDish(item) {
    return Boolean(item && item.name && !this._inTaxFuzzy('drinks', item.name));
  }

  _guessBaseDrink(phrase) {
    const nameRaw = this._stemDrinkName(phrase);
    if (!nameRaw) return null;
    const nameCanon = this._canonicalDrinkName(nameRaw);
    if (this._inTaxFuzzy('drinks', nameCanon)) {
      return { name: nameCanon, quantity: this._extractQuantity(phrase) };
    }
    return null;
  }

  _stemDishName(s) {
    const cleaned = String(s || '')
      .replace(/\b(\d+(\.\d+)?)\s*(cups?|cup|slices?|slice|pieces?|eggs?|tbsp|tablespoons?|tsp|teaspoons?|oz|ounces?|ml|g|grams?)\b/gi, '')
      .replace(/\b(a|an|the|of)\b/gi, '')
      .replace(/\b(with|and|served|topped|made)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    return this._collapseSpaces(cleaned) || s;
  }

  _stemDrinkName(s) {
    const cleaned = String(s || '')
      .replace(/\b(a|an|the|of|glass|cup|mug|bottle|splash|can|shot|pint|tumbler)\b/gi, '')
      .replace(/\b(with|and)\b/gi, '')
      .replace(/[–—-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || null;
  }

  _canonicalDrinkName(n) {
    const l = this._fold(n);
    const map = this._drinkSynonyms();
    if (map[l]) return map[l];

    if (/^(still|tap)\s*water$/.test(l)) return 'water';
    if (/^(sparkling|mineral|soda)\s*water$/.test(l)) return 'sparkling water';
    if (/^nitro\s*cold\s*brew$/.test(l)) return 'nitro cold brew';
    if (/^cold\s*brew$/.test(l)) return 'cold brew';
    if (/^(oj|orange\s*juice)$/.test(l)) return 'orange juice';
    if (/^(apple\s*juice)$/.test(l)) return 'apple juice';
    if (/^(lemonade)$/.test(l)) return 'lemonade';
    if (/^hot\s*(chocolate|cocoa)$/.test(l)) return 'hot chocolate';
    if (/^bubble\s*tea$/.test(l)) return 'bubble tea';

    return n;
  }

  _extractQuantity(s) {
    const str = String(s || '');

    if (/\ba\s+cup\s+of\b/i.test(str)) return '1 cup';
    if (/\ba\s+glass\s+of\b/i.test(str)) return '1 glass';
    if (/\ba\s+slice\s+of\b/i.test(str)) return '1 slice';

    const m = str.match(/\b\d+(\.\d+)?\s*(cups?|cup|slices?|slice|pieces?|eggs?|tbsp|tablespoons?|tsp|teaspoons?|oz|ounces?|ml|g|grams?)\b/gi);
    if (!m) {
      const words = str.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten)\s+(eggs?|slices?|cups?)\b/i);
      return words ? words[0].toLowerCase() : null;
    }
    return m.join(', ').toLowerCase();
  }

  _normalizeQuantityForDrinks(quantity, drinkName) {
    if (!quantity) return null;
    if (/^water$/i.test(drinkName)) return null;
    return quantity.toLowerCase();
  }

  _isQuantityPhrase(s) {
    return /\b(\d+(\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)\s*(cups?|cup|slices?|slice|pieces?|eggs?|tbsp|tablespoons?|tsp|teaspoons?|oz|ounces?|ml|g|grams?)\b/i.test(s);
  }

  _isSeasoning(s) {
    const l = String(s || '').toLowerCase();
    return this._inTax('seasonings', l);
  }

  _isDrinkModifier(s) {
    const l = String(s || '').toLowerCase();
    if (this._inTaxFuzzy('drink_modifiers', l)) return true;
    if (/\b(decaf|iced|ice|hot|cold|unsweetened|sweetened|no\s*sugar|extra\s*shot|double\s*shot|triple\s*shot|light\s*ice|no\s*ice|with\s*ice|on\s*the\s*rocks)\b/.test(l)) return true;
    return this._isQuantityPhrase(l);
  }

  _rebalanceMainsSides(mains, sides) {
    if (mains.length > 0) return { mains, sides };
    const entreeIdx = sides.findIndex(x => /\beggs?|omelette|pancakes?|waffles?|burrito|sandwich|bowl\b/i.test(x.name));
    if (entreeIdx >= 0) {
      const promoted = sides[entreeIdx];
      const rest = sides.filter((_, i) => i !== entreeIdx);
      return { mains: [promoted], sides: rest };
    }
    return { mains, sides };
  }

  // ---------- Taxonomy & fuzzy matching ----------

  static getDefaultTaxonomy() {
    // This is a fallback only for local dev/tests. In production, inject taxonomy via constructor or env JSON.
    return {
      drinks: [
        'coffee','espresso','latte','cappuccino','americano','mocha','macchiato','flat white','cold brew','nitro cold brew','frappuccino','affogato',
        'tea','green tea','black tea','herbal tea','earl grey','oolong','matcha','chai','iced tea','bubble tea','boba',
        'water','sparkling water','mineral water','seltzer','club soda','tonic water',
        'milk','oat milk','almond milk','soy milk','rice milk','coconut milk',
        'juice','orange juice','apple juice','grapefruit juice','lemonade','smoothie','kombucha',
        'soda','cola','coke','pepsi','sprite','fanta','ginger ale','root beer',
        'energy drink','red bull','monster','prime','sports drink','gatorade','powerade','protein shake','milkshake',
        'hot chocolate','cocoa'
      ],
      drink_modifiers: [
        'milk','creamer','cream','half and half','whipped cream','foam','no foam',
        'oat milk','almond milk','soy milk','coconut milk','skim milk','whole milk','2% milk',
        'sugar','sweetener','stevia','honey','syrup','maple syrup','agave',
        'lemon','lime','mint','ginger','cinnamon','vanilla',
        'iced','ice','hot','cold','decaf','extra shot','double shot','triple shot',
        'splash','dash','slice','rocks','light ice','no ice','with ice'
      ],
      seasonings: ['salt', 'pepper', 'chili flakes', 'chilli flakes', 'hot sauce'],
      synonyms: {
        'h2o': 'water',
        'oj': 'orange juice',
        'coke zero': 'cola',
        'coca cola zero': 'cola',
        'pepsi max': 'cola',
        'zero sugar cola': 'cola',
        'diet coke': 'cola',
        'diet cola': 'cola',
        'citrus soda': 'soda',
        'club soda': 'sparkling water',
        'soda water': 'sparkling water',
        'mineral water': 'sparkling water',
        'sparkling h2o': 'sparkling water',
        'protein smoothie': 'protein shake',
        'boba tea': 'bubble tea',
        'milk tea': 'bubble tea'
      }
    };
  }

  _drinkSynonyms() {
    // Prefer taxonomy.synonyms if provided
    const fromTax = (this.taxonomy && this.taxonomy.synonyms) || {};
    return fromTax;
  }

  _fold(s) {
    try {
      return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    } catch {
      return String(s || '').toLowerCase().trim();
    }
  }

  _inTax(key, value) {
    const list = (this.taxonomy && this.taxonomy[key]) || [];
    const v = String(value || '').toLowerCase();
    return list.some(x => v.includes(String(x).toLowerCase()));
  }

  _inTaxFuzzy(key, value) {
    const list = (this.taxonomy && this.taxonomy[key]) || [];
    if (!list || list.length === 0) return false;
    const v = this._fold(value);
    // fast path: substring check
    for (const t of list) {
      const lt = this._fold(t);
      if (v.includes(lt) || lt.includes(v)) return true;
    }
    // synonym path for drinks/modifiers
    if (key === 'drinks' || key === 'drink_modifiers') {
      const map = this._drinkSynonyms();
      if (map[v]) return true;
    }
    // edit-distance fallback (deterministic)
    let best = Infinity;
    for (const t of list) {
      const lt = this._fold(t);
      const d = this._levenshtein(v, lt);
      if (d < best) best = d;
      if (d <= 1) return true; // near exact
    }
    // length-aware threshold (tolerant for typos)
    const L = Math.max(v.length, 1);
    return best <= Math.max(2, Math.floor(L * 0.25));
  }

  _levenshtein(a, b) {
    if (a === b) return 0;
    const n = a.length, m = b.length;
    if (n === 0) return m;
    if (m === 0) return n;
    const dp = new Array(m + 1);
    for (let j = 0; j <= m; j++) dp[j] = j;
    for (let i = 1; i <= n; i++) {
      let prev = dp[0];
      dp[0] = i;
      for (let j = 1; j <= m; j++) {
        const tmp = dp[j];
        if (a[i - 1] === b[j - 1]) {
          dp[j] = prev;
        } else {
          dp[j] = Math.min(prev + 1, dp[j] + 1, dp[j - 1] + 1);
        }
        prev = tmp;
      }
    }
    return dp[m];
  }

  _toCanonical(s) {
    return this._collapseSpaces(String(s || '').trim());
  }

  _stripLeadingArticles(s) {
    return String(s || '').replace(/^\s*(a|an|the)\s+/i, '').trim();
  }

  _collapseSpaces(s) {
    return s.replace(/\s+/g, ' ').trim();
  }

  _utcMidnightIso(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)).toISOString();
  }

  _newEvent({ ingress, egress, payload, requestId, traceId, visited, hopCount, idempotencyKey, changeHash, entityId }) {
    return {
      id: this.idProvider(),
      type: 'categorize_text',
      source: this.serviceName,
      ingress,
      egress,
      requestId: requestId || this.idProvider(),
      traceId: traceId || this.idProvider(),
      visited: Array.isArray(visited) ? [...visited] : [],
      hopCount: Number(hopCount || 0),
      publishedAt: this.timeProvider().toISOString(),
      idempotencyKey: idempotencyKey || null,
      changeHash: changeHash || null,
      entityId: entityId || null,
      payload: payload || {},
    };
  }

  _resultSkeleton({ reason }, eventMeta) {
    return {
      accepted: false,
      reason: reason || 'invalid',
      timestampIso: this._utcMidnightIso(this.timeProvider()),
      categories: { mains: [], sides: [], drinks: [], unknown: [] },
      meta: eventMeta ? { event: sanitizeEventForReturn(eventMeta) } : undefined
    };
  }
}

// Convenience singleton (optional; override via injected config as needed)
const mealCategorizer = new AiMealCategorizer({
  serviceName: process.env.AI_MEAL_CATEGORIZER_SERVICE_NAME || 'AiMealCategorizer',
  maxHopCount: Number(process.env.AI_MEAL_CATEGORIZER_MAX_HOPS || 8),
  allowedRoutes: safeJson(process.env.AI_MEAL_CATEGORIZER_ALLOWED_ROUTES) || { categorizer_ingress: ['categorizer_egress'] },
  taxonomy: safeJson(process.env.AI_MEAL_CATEGORIZER_TAXONOMY_JSON) || AiMealCategorizer.getDefaultTaxonomy(),
  timeProvider: () => new Date(),
  idProvider: defaultUlid,
  idempotency: { tryStart: () => true, markComplete: () => {} }
});

export { AiMealCategorizer };
export default mealCategorizer;

// -------------------- Local helpers (simple, descriptive) ---------------------

function sanitizeEventForReturn(e) {
  // Only return safe, non-secret fields
  const {
    id, type, source, ingress, egress, requestId, traceId,
    visited, hopCount, publishedAt, idempotencyKey, changeHash, entityId
  } = e || {};
  return { id, type, source, ingress, egress, requestId, traceId, visited, hopCount, publishedAt, idempotencyKey, changeHash, entityId };
}

function defaultUlid() {
  // Monotonic-ish, no external deps; good enough for tags
  const now = Date.now().toString(36).padStart(9, '0');
  const rand = (typeof crypto !== 'undefined' && crypto.getRandomValues)
    ? Array.from(crypto.getRandomValues(new Uint8Array(10))).map(b => (b % 36).toString(36)).join('')
    : Math.random().toString(36).slice(2, 12).padEnd(10, '0');
  return `${now}${rand}`.slice(0, 19);
}

function safeJson(str) {
  if (!str) return null;
  try { return JSON.parse(str); } catch { return null; }
}
